import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {CommonModalComponent} from '../../../../shared/common-modal/common-modal.component';
import {OfferDataManagementService} from '../../offer-data-management.service';
import {Range} from '../../../../shared/model/range-model';
import {BslReference} from '../../../shared/bsl-reference/bsl-reference-model';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {LoggerService} from '../../../../shared/logging/logger.service';
import {CubicleSld} from '../../../shared/bsl-reference/cubicle-sld-model';
import {CubicleLayout} from '../../../shared/bsl-reference/cubicle-layout-model';
import {OfferDataManagementHelper} from '../../../shared/util/offer-data-management-helper';
import {RangeType} from "../../../../shared/model/range-type";
import {Reference} from "../../../../configuration/shared/model/reference";
import {PartialObserver} from "rxjs";

@Component({
  standalone: false,
  selector: 'app-odm-add-modal',
  templateUrl: './odm-add-modal.component.html',
  styleUrls: ['./odm-add-modal.component.less'],
})
export class OdmAddModalComponent {

  @ViewChild('addReferenceModal', {static: false}) addReferenceModal: CommonModalComponent;

  @Input()
  selectedRange: Range;
  @Input()
  allowedReferenceCategories: String[] = [];
  @Input()
  notAllowedReferenceCategories: String[] = [];

  @Input()
  isOption: boolean;

  @Input()
  accessType: string;

  @Output() onAddReferenceButton = new EventEmitter();

  allBslReferences: BslReference[] = [];
  showList = true;
  filteredList: string[] = [];
  reference = '';
  loading = false;
  showError: boolean = false;
  isModifyReferenceMode: boolean;

  @ViewChild('sldModal', {static: false}) sldModal: CommonModalComponent;
  @ViewChild('layoutModal', {static: false}) layoutModal: CommonModalComponent;

  sldList: CubicleSld[];
  selectedSld: CubicleSld[];
  loadingSld = false;
  layoutList: CubicleLayout[];
  selectedLayout: CubicleLayout[];
  loadingLayout = false;
  newReference: string = '';

  constructor(private readonly offerDataManagementService: OfferDataManagementService,
              private messageService: MessageService,
              private readonly translate: TranslateService,
              private readonly logger: LoggerService) {
  }


  /**
   * Reset the form
   */
  resetForm() {
    this.newReference = '';
    this.showError = false;
    this.isModifyReferenceMode = false;
  }

  /**
   * Show the modal
   */
  show() {
    this.loading = true;
    this.showList = true;
    this.addReferenceModal.show();
    this.allBslReferences = [];
    this.filteredList = [];
    this.selectedSld = [];
    this.selectedLayout = [];
    this.reference = '';
    if (this.selectedRange) {
      this.offerDataManagementService.getCommercialReferencesForRangeId(this.selectedRange.id, this.accessType).subscribe(
        references => {
          this.allBslReferences = this.getFilteredReferences(references);
          this.filteredList = this.allBslReferences.map(bslRef => bslRef.reference);
          this.loading = false;
        },
        () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('T_ERROR'),
            detail: this.translate.instant('T_OFFER_DATA_MANAGEMENT_ERROR_REFERENCE'),
          });
        }
      );
    }
  }

  private getFilteredReferences(references: BslReference[]) {
    return references.filter(ref => {
      if (!ref.category) {
        return true;
      }
      if (this.allowedReferenceCategories.length > 0) {
        return this.allowedReferenceCategories.includes(ref.category);
      }
      return !this.notAllowedReferenceCategories.includes(ref.category);
    })
  }

  /**
   * Function called to filter references on the list when user changes the reference input
   */
  filterReferences() {
    this.filteredList = this.allBslReferences
      .filter(ref => ref.reference.toUpperCase().includes(this.reference.toUpperCase()))
      .map(ref => ref.reference);
  }

  /**
   * function called when user clicks on search icon
   */
  search() {
    this.showList = !this.showList;
    this.filterReferences();
  }

  /**
   * Function to hide the modal
   */
  hide() {
    this.addReferenceModal.hide();
    this.reference = '';
    this.selectedSld = [];
    this.selectedLayout = [];
    this.resetForm();
  }

  /**
   * Function called when user clicks on added reference
   */
  addReference() {
    if (this.isValidForm()) {
      this.onAddReferenceButton.emit({bslReference: this.getBslReferenceFromReference(),
        sld: this.selectedSld, layout: this.selectedLayout, isModifyReferenceMode: this.isModifyReferenceMode});
      this.hide();
    }
  }


  /**
   * Function called when user clicks on add SLD & Layout
   * Update sld/layout of a reference
   */
  showModifyReferenceModal(reference: Reference, sldList: CubicleSld[], layoutList: CubicleLayout[]) {
    this.newReference = reference?.ref
    this.isModifyReferenceMode = true;
    this.selectedSld = sldList;
    this.selectedLayout = layoutList;
    this.addReferenceModal.show();
  }


  /**
   * Function called when user click on add SLD
   * Get sld list by range id.
   */
  getSLDList() {
    this.sldList = [];
    this.loadingSld = true;

    const observer: PartialObserver<CubicleSld[]> = {
      next: (allSld) => {
        this.sldList = allSld;
        this.loadingSld = false;
      },
      error: (error) => {
        this.loadingSld = false;
        this.logger.error(error);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('T_ERROR'),
          detail: this.translate.instant('T_OFFER_DATA_MANAGEMENT_SLD_RETRIEVING_ERROR'),
        });
      }
    };

    if (this.isBudgetaryQuoteRange()) {
      const sldRangeId = this.selectedRange.id;
      this.offerDataManagementService.getSLDsForRange(sldRangeId.toString())
        .subscribe(observer);
    } else {
      const bslRangeId = this.selectedRange.bslRangeId;
      this.offerDataManagementService.getAllSLDByRangeId(bslRangeId)
        .subscribe(observer);
    }
    this.sldModal.show();
  }

  /**
   * Get sld to add on reference
   * @param sld
   */
  getSldToAddOnReference(sld) {
    this.selectedSld = sld ? sld : '';
    this.sldModal.hide();
  }


  /**
   * Function to check if the form is valid
   * It is valid if the reference selected if unique
   * and if it is not a cubicle.
   * If it is the cubicle, it has to have a sld selected
   */
  isValidForm(): boolean {
    const hasSelectedSld = this.selectedSld != null && this.selectedSld.length !== 0;
    // Update reference sld & layout for modify reference mode, SLD must be selected
    if (this.isModifyReferenceMode) {
      return hasSelectedSld;
    }
    if (this.isReferenceInputEnabled()) {
      let alphanumericPattern = /^[a-zA-Z0-9 _\-]*$/;
      return (this.newReference.length > 0 && alphanumericPattern.test(this.newReference));
    } else {
      return (this.isReferenceIncludedInListAndUnique())
        && (hasSelectedSld || !this.showSldAndLayoutSelector());
    }
  }

  /**
   * Return true if the reference object exists in all references lists and is unique
   * @returns {boolean}
   */
  isReferenceIncludedInListAndUnique(): boolean {
    return this.allBslReferences
      .filter(ref => ref.reference.toUpperCase() === this.reference.toUpperCase()).length === 1;
  }

  /**
   * Return BSL reference corresponding to the reference selected by user
   * @returns {BslReference}
   */
  getBslReferenceFromReference(): BslReference {
    if (this.isReferenceInputEnabled()) {
      return {
        reference: this.newReference,
        category: '',
        description: ''
      };
    } else {
      return this.allBslReferences
        .filter(ref => ref.reference.toUpperCase() === this.reference.toUpperCase())[0];
    }
  }

  /**
   * Return true if a reference is selected and is a cubicle
   * @returns {boolean} true if the reference selected is a cubicle
   */
  isCubicleSelected(): boolean {
    return this.isReferenceIncludedInListAndUnique() &&
      (this.getBslReferenceFromReference().category === 'Cubicle'
        || this.getBslReferenceFromReference().category === 'Switchboard'
        || this.getBslReferenceFromReference().category === 'Three Functions'
        || this.getBslReferenceFromReference().category === 'Single Function'
        || this.getBslReferenceFromReference().category === 'Ring Main Unit'
        || this.getBslReferenceFromReference().category === 'Commercial reference'
        || this.getBslReferenceFromReference().category === 'Commercial References'
        || this.getBslReferenceFromReference().category === 'Generic References');
  }

  /**
   * Used to display the list of selected SLD in human-readable format
   * @returns {string}
   */
  displaySldList(): string {
    return this.selectedSld != null ? this.selectedSld.map(sld => this.translate.instant(sld.nameKey)).join('-') : '';
  }


  /**
   * Used to display a list of selected Layout in human-readable format
   * @returns {string}
   */
  displayLayoutList(): string {
    return this.selectedLayout != null ?
      this.selectedLayout.map(layout => OfferDataManagementHelper.getSvgFileNameWithoutExtension(layout.pictureFileName)).join('-')
      : '';
  }

  /**
   * Function called when user clicks on added Layout
   * Get a layout list by range id.
   */
  getLayoutList() {
    this.layoutList = [];
    this.loadingLayout = true;
    let fetchByBslRange = true;
    let rangeId = this.selectedRange.bslRangeId;
    if (this.isBudgetaryQuoteRange()) {
      fetchByBslRange = false;
      rangeId = this.selectedRange.id
    }
    this.offerDataManagementService.getAllLayoutByRangeId(rangeId?.toString(), fetchByBslRange).subscribe(
      allLayout => {
        this.layoutList = allLayout;
        this.loadingLayout = false;
      },
      error => {
        this.loadingLayout = false;
        this.logger.error(error);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('T_ERROR'),
          detail: this.translate.instant('T_OFFER_DATA_MANAGEMENT_LAYOUT_RETRIEVING_ERROR'),
        });
      }
    );
    this.layoutModal.show();
  }

  /**
   * Get layout to add on reference
   * @param layout
   */
  getLayoutToAddOnReference(layout) {
    this.selectedLayout = layout ? layout : '';
    this.layoutModal.hide();
  }

  showSldAndLayoutSelector() {
    return (this.isCubicleSelected() && this.selectedRange.bslRangeId) || this.isModifyReferenceMode;
  }

  isBudgetaryQuoteRange() {
    return this.selectedRange?.rangeType === RangeType.BUDJETARY_QUOTE && !this.isOption;
  }

  isTransformSelectedRange() {
    return this.selectedRange?.importedRange && this.selectedRange?.useTransformerSelector && !this.isOption;
  }

  isReferenceInputEnabled() {
    return (this.selectedRange?.rangeType === RangeType.BUDJETARY_QUOTE || this.isTransformSelectedRange() || this.isModifyReferenceMode)  && !this.isOption;
  }


  validateInput(value: string): void {
    this.newReference = value;
    this.showError = !this.isReferenceValid();
  }

  isReferenceValid(): boolean {
    const alphanumericPattern = /^[a-zA-Z0-9 _\-]*$/;
    return this.newReference.length > 0 && alphanumericPattern.test(this.newReference);
  }
}
